import React from 'react';
import { connect } from 'react-redux';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  Tabs, Tab, TextField, Switch, FormControlLabel, Divider, MenuItem, Select, InputLabel, FormControl,
} from '@mui/material';
import AceEditor from 'react-ace';
import 'ace-builds/webpack-resolver';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/theme-monokai';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { getPartnerGuid, hasAuthourity } from '../../lib/access.es6';
import {
  getGatewayConfigsRequest,
  getGatewayConfigRawRequest,
  getGatewayConfigRawReset,
  publishGatewayConfigRequest,
  publishGatewayConfigReset,
  createGatewayConfigRequest,
  createGatewayConfigReset,
} from './actions.es6';

var TH = {
  fontSize: 11, fontWeight: 700, color: '#6B7280',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
  py: 1.25, px: 2,
};
var TD = {
  fontSize: 13, color: '#374151',
  borderBottom: '1px solid #F3F4F6',
  py: 1.5, px: 2,
};

var AUTH_TYPES = ['oauth2_client_credentials', 'basic', 'basic_auth', 'bearer_custom', 'none'];
var HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
var PROTOCOLS = ['json', 'xml', 'soap'];

function parseYaml(str) {
  try {
    var jsYaml = require('js-yaml');
    var load = jsYaml.load || jsYaml.safeLoad;
    return load(str) || {};
  } catch (e) {
    return null;
  }
}

function dumpYaml(obj) {
  try {
    var jsYaml = require('js-yaml');
    var dump = jsYaml.dump || jsYaml.safeDump;
    return dump(obj, { lineWidth: 120, noRefs: true });
  } catch (e) {
    return '';
  }
}

function applyFormChange(yamlStr, updater) {
  var parsed = parseYaml(yamlStr);
  if (!parsed) return yamlStr;
  updater(parsed);
  return dumpYaml(parsed);
}

function newGatewayConfigTemplate(partner) {
  return [
    `id: ${partner}-gateway`,
    `partner: ${partner}`,
    `channel: mobile`,
    `type: mobile_money`,
    `enabled: true`,
    ``,
  ].join('\n');
}

class GatewayConfigs extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editorOpen: false,
      isNew: false,
      selectedConfig: null,
      editorContent: '',
      hasYamlErrors: false,
      activeTab: 0,
    };
    this.patch = this.patch.bind(this);
  }

  patch(updater) {
    // Run applyFormChange synchronously so closures over synthetic events fire
    // before React 16 pools them (e.target would be null inside a deferred updater).
    this.setState({ editorContent: applyFormChange(this.state.editorContent, updater) });
  }

  componentWillMount() {
    this.props.dispatch(getGatewayConfigsRequest(getPartnerGuid()));
  }

  componentDidUpdate(prevProps) {
    const { gateway_config_raw, successfully_published, successfully_created } = this.props;

    if (gateway_config_raw && gateway_config_raw !== prevProps.gateway_config_raw) {
      this.setState({ editorContent: gateway_config_raw });
    }
    if (successfully_published && successfully_published !== prevProps.successfully_published) {
      this.closeEditor();
    }
    if (successfully_created && successfully_created !== prevProps.successfully_created) {
      this.closeEditor();
    }
  }

  openEdit(config) {
    this.props.dispatch(getGatewayConfigRawReset());
    this.props.dispatch(publishGatewayConfigReset());
    this.setState({ editorOpen: true, isNew: false, selectedConfig: config, editorContent: '', activeTab: 0 });
    this.props.dispatch(getGatewayConfigRawRequest(getPartnerGuid(), config.id));
  }

  openCreate() {
    this.props.dispatch(getGatewayConfigRawReset());
    this.props.dispatch(createGatewayConfigReset());
    this.setState({
      editorOpen: true,
      isNew: true,
      selectedConfig: null,
      editorContent: newGatewayConfigTemplate(getPartnerGuid()),
      activeTab: 0,
    });
  }

  closeEditor() {
    this.setState({ editorOpen: false, selectedConfig: null, editorContent: '', activeTab: 0 });
    this.props.dispatch(getGatewayConfigRawReset());
    this.props.dispatch(publishGatewayConfigReset());
    this.props.dispatch(createGatewayConfigReset());
  }

  handlePublish() {
    const { editorContent, isNew, selectedConfig } = this.state;
    const partner = getPartnerGuid();
    if (isNew) {
      this.props.dispatch(createGatewayConfigRequest(partner, editorContent));
    } else {
      this.props.dispatch(publishGatewayConfigRequest(partner, selectedConfig.id, editorContent));
    }
  }

  renderUiTab() {
    const { editorContent, isNew } = this.state;
    const parsed = parseYaml(editorContent);
    if (!parsed) {
      return (
        <Alert severity="warning">
          YAML has syntax errors — switch to the YAML tab to fix them before editing here.
        </Alert>
      );
    }

    var auth = parsed.auth || {};
    var initiate = parsed.initiate || {};
    var initiateSteps = Array.isArray(initiate.steps) ? initiate.steps : [];
    var status = parsed.status || {};
    var statusResponse = status.response || {};
    var statusMap = statusResponse.status_map || {};

    var patchAuth = (updater) => {
      this.patch(p => { if (!p.auth || typeof p.auth !== 'object') p.auth = {}; updater(p.auth); });
    };
    var patchInitiate = (updater) => {
      this.patch(p => { if (!p.initiate || typeof p.initiate !== 'object') p.initiate = {}; updater(p.initiate); });
    };
    var patchStatus = (updater) => {
      this.patch(p => { if (!p.status || typeof p.status !== 'object') p.status = {}; updater(p.status); });
    };
    var patchStatusResponse = (updater) => {
      patchStatus(s => { if (!s.response || typeof s.response !== 'object') s.response = {}; updater(s.response); });
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Basic info */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280' }}>
            Basic info
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5, alignItems: 'flex-end' }}>
            <TextField label="ID" size="small" value={parsed.id || ''} disabled={!isNew}
              onChange={e => this.patch(p => { p.id = e.target.value; })}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 200 }} />
            <TextField label="Channel" size="small" value={parsed.channel || ''}
              onChange={e => this.patch(p => { p.channel = e.target.value || undefined; })} sx={{ width: 140 }} />
            <TextField label="Type" size="small" value={parsed.type || ''}
              onChange={e => this.patch(p => { p.type = e.target.value || undefined; })}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 160 }} />
            <FormControlLabel control={
              <Switch checked={!!parsed.enabled} onChange={e => this.patch(p => { p.enabled = e.target.checked; })} size="small" />
            } label={<Typography variant="body2">Enabled</Typography>} />
          </Box>
        </Box>

        <Divider />

        {/* Protocol */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280' }}>
            Protocol & connectivity
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5, alignItems: 'center' }}>
            <TextField select label="Protocol" size="small" value={parsed.protocol || 'json'}
              onChange={e => this.patch(p => { p.protocol = e.target.value !== 'json' ? e.target.value : undefined; })}
              sx={{ width: 120 }}>
              {PROTOCOLS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
            <TextField label="Timeout (ms)" size="small" type="number" value={parsed.timeout_ms || ''}
              placeholder="30000"
              onChange={e => this.patch(p => { p.timeout_ms = e.target.value ? Number(e.target.value) : undefined; })}
              sx={{ width: 130 }} />
            <FormControlLabel control={
              <Switch checked={!!parsed.strip_namespaces} onChange={e => this.patch(p => { if (e.target.checked) p.strip_namespaces = true; else delete p.strip_namespaces; })} size="small" />
            } label={<Typography variant="body2">Strip XML namespaces</Typography>} />
            <FormControlLabel control={
              <Switch checked={!!parsed.tls_skip_verify} onChange={e => this.patch(p => { if (e.target.checked) p.tls_skip_verify = true; else delete p.tls_skip_verify; })} size="small" />
            } label={<Typography variant="body2">Skip TLS verify</Typography>} />
          </Box>
        </Box>

        <Divider />

        {/* Auth */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280' }}>
            Authentication
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <TextField select label="Auth type" size="small" value={auth.type || 'none'}
              onChange={e => patchAuth(a => { a.type = e.target.value; })} sx={{ width: 240 }}>
              {AUTH_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Box>
          {auth.type === 'oauth2_client_credentials' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
              <TextField label="Token URL" size="small" value={auth.token_url || ''} placeholder="https://..."
                onChange={e => patchAuth(a => { a.token_url = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 280 }} />
              <TextField label="Client ID" size="small" value={(auth.credentials || {}).client_id || ''}
                onChange={e => patchAuth(a => { if (!a.credentials) a.credentials = {}; a.credentials.client_id = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 200 }} />
              <TextField label="Client secret" size="small" value={(auth.credentials || {}).client_secret || ''}
                onChange={e => patchAuth(a => { if (!a.credentials) a.credentials = {}; a.credentials.client_secret = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 200 }} />
              <TextField label="Cache key" size="small" value={auth.cache_key || ''}
                onChange={e => patchAuth(a => { a.cache_key = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 160 }} />
              <TextField label="Cache TTL field" size="small" value={auth.cache_ttl_from || ''} placeholder="expires_in"
                onChange={e => patchAuth(a => { a.cache_ttl_from = e.target.value || undefined; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 140 }} />
            </Box>
          )}
          {auth.type === 'basic' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
              <TextField label="Key" size="small" value={auth.key || ''}
                onChange={e => patchAuth(a => { a.key = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 200 }} />
              <TextField label="Secret" size="small" value={auth.secret || ''}
                onChange={e => patchAuth(a => { a.secret = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 200 }} />
            </Box>
          )}
          {auth.type === 'basic_auth' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
              <TextField label="Token URL" size="small" value={auth.token_url || ''}
                onChange={e => patchAuth(a => { a.token_url = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 280 }} />
              <TextField label="Username" size="small" value={(auth.credentials || {}).username || ''}
                onChange={e => patchAuth(a => { if (!a.credentials) a.credentials = {}; a.credentials.username = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 180 }} />
              <TextField label="Password" size="small" value={(auth.credentials || {}).password || ''}
                onChange={e => patchAuth(a => { if (!a.credentials) a.credentials = {}; a.credentials.password = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 180 }} />
              <TextField label="Cache key" size="small" value={auth.cache_key || ''}
                onChange={e => patchAuth(a => { a.cache_key = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 160 }} />
            </Box>
          )}
          {auth.type === 'bearer_custom' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
              <TextField label="Token URL" size="small" value={auth.token_url || ''}
                onChange={e => patchAuth(a => { a.token_url = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 280 }} />
              <TextField label="Token path" size="small" value={auth.token_path || ''} placeholder="access_token"
                onChange={e => patchAuth(a => { a.token_path = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 160 }} />
              <TextField label="Cache key" size="small" value={auth.cache_key || ''}
                onChange={e => patchAuth(a => { a.cache_key = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 140 }} />
              <TextField label="Cache TTL (s)" size="small" type="number" value={auth.cache_ttl || ''}
                onChange={e => patchAuth(a => { a.cache_ttl = e.target.value ? Number(e.target.value) : undefined; })} sx={{ width: 120 }} />
              <TextField select label="Token method" size="small" value={auth.token_method || 'GET'}
                onChange={e => patchAuth(a => { a.token_method = e.target.value !== 'GET' ? e.target.value : undefined; })} sx={{ width: 110 }}>
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
              </TextField>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Initiate steps */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280' }}>
                Initiate flow — steps ({initiateSteps.length})
              </Typography>
              <TextField label="Pre-hook" size="small" value={initiate.pre_hook || ''} placeholder="optional"
                onChange={e => patchInitiate(i => { i.pre_hook = e.target.value || undefined; })} sx={{ width: 160 }} />
            </Box>
            <Button size="small" startIcon={<AddIcon />} variant="outlined" sx={{ textTransform: 'none', fontSize: 12 }}
              onClick={() => patchInitiate(i => {
                if (!Array.isArray(i.steps)) i.steps = [];
                i.steps.push({ id: '', method: 'POST', url: '' });
              })}>
              Add step
            </Button>
          </Box>
          {initiateSteps.length > 0 ? (
            <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 1, overflow: 'hidden' }}>
              {initiateSteps.map((s, i) => this.renderGatewayStepCard(s, i, (updater) => {
                patchInitiate(ini => { updater(ini.steps); });
              }))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: 12 }}>No initiate steps defined.</Typography>
          )}
        </Box>

        <Divider />

        {/* Status check */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280' }}>
            Status check
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mt: 1.5 }}>
            <FormControlLabel control={
              <Switch checked={!!status.polling} onChange={e => patchStatus(s => { s.polling = e.target.checked; })} size="small" />
            } label={<Typography variant="body2">Polling enabled</Typography>} />
            <TextField label="Delay (ms)" size="small" type="number" value={status.delay_ms || ''}
              onChange={e => patchStatus(s => { s.delay_ms = e.target.value ? Number(e.target.value) : undefined; })} sx={{ width: 120 }} />
            <TextField select label="Method" size="small" value={status.method || 'GET'}
              onChange={e => patchStatus(s => { s.method = e.target.value; })} sx={{ width: 100 }}>
              <MenuItem value="GET">GET</MenuItem>
              <MenuItem value="POST">POST</MenuItem>
            </TextField>
            <TextField label="URL" size="small" value={status.url || ''} placeholder="https://..."
              onChange={e => patchStatus(s => { s.url = e.target.value || undefined; })}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 11 } }} sx={{ width: 300 }} />
          </Box>
          {status.polling && (
            <Box sx={{ mt: 2, border: '1px solid #E5E7EB', borderRadius: 1, p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280', fontSize: 10 }}>
                Response mapping
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                <TextField label="Status path" size="small" value={statusResponse.status_path || ''} placeholder="data.status"
                  onChange={e => patchStatusResponse(r => { r.status_path = e.target.value; })}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 200 }} />
                <TextField label="Feedback path" size="small" value={statusResponse.feedback_path || ''} placeholder="data.message"
                  onChange={e => patchStatusResponse(r => { r.feedback_path = e.target.value || undefined; })}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 200 }} />
                <TextField label="Transaction ID path" size="small" value={statusResponse.partner_transaction_id_path || ''}
                  onChange={e => patchStatusResponse(r => { r.partner_transaction_id_path = e.target.value || undefined; })}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} sx={{ width: 200 }} />
              </Box>
              {/* Status map */}
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280', fontSize: 10 }}>
                    Status map (provider → internal)
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} variant="text" sx={{ textTransform: 'none', fontSize: 11, minWidth: 0 }}
                    onClick={() => patchStatusResponse(r => {
                      if (!r.status_map || typeof r.status_map !== 'object') r.status_map = {};
                      r.status_map[''] = 'Succeeded';
                    })}>
                    Add
                  </Button>
                </Box>
                {Object.keys(statusMap).length > 0 ? (
                  <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 1, overflow: 'hidden' }}>
                    {Object.entries(statusMap).map(([k, v], i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderBottom: i < Object.keys(statusMap).length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                        <input style={{ width: 100, fontFamily: 'monospace', fontSize: 12, border: 'none', outline: 'none', background: 'transparent' }}
                          value={k} placeholder="TS"
                          onChange={e => patchStatusResponse(r => {
                            var m = r.status_map; var val = m[k]; delete m[k]; m[e.target.value] = val || 'Succeeded';
                          })} />
                        <Typography sx={{ color: '#9CA3AF', fontSize: 12 }}>→</Typography>
                        <select style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, border: 'none', outline: 'none', background: 'transparent' }}
                          value={v} onChange={e => patchStatusResponse(r => { r.status_map[k] = e.target.value; })}>
                          {['Succeeded', 'Failed', 'InProgress', 'Pending'].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <IconButton size="small" sx={{ color: '#EF4444', opacity: 0.6 }} onClick={() => patchStatusResponse(r => { delete r.status_map[k]; })}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: 11 }}>No status mappings defined.</Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: 11 }}>
          Consent, callback, and advanced fields — edit in the YAML tab.
        </Typography>
      </Box>
    );
  }

  renderGatewayStepCard(step, index, patchSteps) {
    var self = this;

    function stepPatch(updater) {
      patchSteps(function(arr) { updater(arr[index]); });
    }

    var headers = (step.headers && typeof step.headers === 'object' && !Array.isArray(step.headers)) ? step.headers : {};
    var extract = (step.extract && typeof step.extract === 'object' && !Array.isArray(step.extract)) ? step.extract : {};
    var asserts = Array.isArray(step.assert) ? step.assert : [];
    var bodyStr = step.body && typeof step.body === 'object' ? JSON.stringify(step.body, null, 2) : (step.body || '');

    return (
      <details key={index} style={{ borderBottom: '1px solid #F3F4F6' }}>
        <summary style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', listStyle: 'none' }}>
          <Typography sx={{ flex: 1, fontFamily: 'monospace', fontSize: 12, fontWeight: 500 }}>
            {step.id || `step_${index + 1}`}
          </Typography>
          <Chip label={step.method || 'POST'} size="small" color="primary" sx={{ fontFamily: 'monospace', fontSize: 10, height: 20 }} />
          <Typography sx={{ fontFamily: 'monospace', fontSize: 10, color: '#9CA3AF', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {step.url || ''}
          </Typography>
          <IconButton size="small" sx={{ color: '#EF4444', opacity: 0.6 }} onClick={(e) => {
            e.preventDefault();
            patchSteps(function(arr) { arr.splice(index, 1); });
          }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </summary>
        <Box sx={{ px: 2, py: 1.5, bgcolor: '#FAFAFA', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
            <TextField label="ID" size="small" fullWidth value={step.id || ''} placeholder="step_id"
              onChange={e => stepPatch(s => { s.id = e.target.value; })}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} />
            <TextField select label="Method" size="small" fullWidth value={step.method || 'POST'}
              onChange={e => stepPatch(s => { s.method = e.target.value; })}>
              {HTTP_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField label="Timeout (ms)" size="small" fullWidth type="number" value={step.timeout_ms || ''}
              onChange={e => stepPatch(s => { s.timeout_ms = e.target.value ? Number(e.target.value) : undefined; })} />
          </Box>
          <TextField label="URL" size="small" fullWidth value={step.url || ''} placeholder="https://..."
            onChange={e => stepPatch(s => { s.url = e.target.value; })}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} />
          <TextField label="Error path" size="small" fullWidth value={step.error_path || ''} placeholder="errorMessage"
            onChange={e => stepPatch(s => { s.error_path = e.target.value || undefined; })}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} />
          {/* Headers */}
          {this.renderKvSection('Headers', headers,
            () => stepPatch(s => { if (!s.headers) s.headers = {}; s.headers[''] = ''; }),
            (ok, nk) => stepPatch(s => { var v = s.headers[ok]; delete s.headers[ok]; s.headers[nk] = v || ''; }),
            (k, v) => stepPatch(s => { s.headers[k] = v; }),
            (k) => stepPatch(s => { delete s.headers[k]; }),
            'Content-Type', 'application/json'
          )}
          {/* Body */}
          <TextField label="Body (JSON or template)" size="small" fullWidth multiline rows={3} value={bodyStr}
            placeholder='{"amount": "{{payment.amount}}"}'
            onChange={e => stepPatch(s => {
              try { s.body = JSON.parse(e.target.value); } catch { s.body = e.target.value || undefined; }
            })}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }} />
          {/* Extract */}
          {this.renderKvSection('Extract (alias → JSONPath)', extract,
            () => stepPatch(s => { if (!s.extract) s.extract = {}; s.extract[''] = ''; }),
            (ok, nk) => stepPatch(s => { var v = s.extract[ok]; delete s.extract[ok]; s.extract[nk] = v || ''; }),
            (k, v) => stepPatch(s => { s.extract[k] = v; }),
            (k) => stepPatch(s => { delete s.extract[k]; }),
            'partner_transaction_id', 'data.transactionId'
          )}
          {/* Assert */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280', fontSize: 10 }}>
                Assert ({asserts.length})
              </Typography>
              <Button size="small" startIcon={<AddIcon />} variant="text" sx={{ textTransform: 'none', fontSize: 11, minWidth: 0 }}
                onClick={() => stepPatch(s => { if (!Array.isArray(s.assert)) s.assert = []; s.assert.push({ path: '', equals: '' }); })}>
                Add
              </Button>
            </Box>
            {asserts.length > 0 && (
              <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 1, overflow: 'hidden' }}>
                {asserts.map((a, ai) => (
                  <Box key={ai} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderBottom: ai < asserts.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                    <input style={{ width: 120, fontFamily: 'monospace', fontSize: 12, border: 'none', outline: 'none', background: 'transparent' }}
                      value={a.path || ''} placeholder="path"
                      onChange={e => stepPatch(s => { s.assert[ai].path = e.target.value; })} />
                    <Typography sx={{ color: '#9CA3AF', fontSize: 12 }}>=</Typography>
                    <input style={{ width: 100, fontFamily: 'monospace', fontSize: 12, border: 'none', outline: 'none', background: 'transparent' }}
                      value={a.equals || ''} placeholder="expected"
                      onChange={e => stepPatch(s => { s.assert[ai].equals = e.target.value; })} />
                    <input style={{ flex: 1, fontFamily: 'monospace', fontSize: 11, border: 'none', outline: 'none', background: 'transparent', opacity: 0.7 }}
                      value={a.error_path || ''} placeholder="error_path (opt)"
                      onChange={e => stepPatch(s => { s.assert[ai].error_path = e.target.value || undefined; })} />
                    <IconButton size="small" sx={{ color: '#EF4444', opacity: 0.6 }} onClick={() => stepPatch(s => { s.assert.splice(ai, 1); })}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </details>
    );
  }

  renderKvSection(label, entries, onAdd, onChangeKey, onChangeVal, onDelete, keyPlaceholder, valPlaceholder) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280', fontSize: 10 }}>
            {label}
          </Typography>
          <Button size="small" startIcon={<AddIcon />} variant="text" sx={{ textTransform: 'none', fontSize: 11, minWidth: 0 }} onClick={onAdd}>
            Add
          </Button>
        </Box>
        {Object.keys(entries).length > 0 ? (
          <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 1, overflow: 'hidden' }}>
            {Object.entries(entries).map(([k, v], i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderBottom: i < Object.keys(entries).length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                <input style={{ width: 150, fontFamily: 'monospace', fontSize: 12, border: 'none', outline: 'none', background: 'transparent' }}
                  value={k} placeholder={keyPlaceholder} onChange={e => onChangeKey(k, e.target.value)} />
                <Typography sx={{ color: '#9CA3AF', fontSize: 12 }}>→</Typography>
                <input style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, border: 'none', outline: 'none', background: 'transparent' }}
                  value={v} placeholder={valPlaceholder} onChange={e => onChangeVal(k, e.target.value)} />
                <IconButton size="small" sx={{ color: '#EF4444', opacity: 0.6 }} onClick={() => onDelete(k)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: 11 }}>None defined.</Typography>
        )}
      </Box>
    );
  }

  render() {
    if (!hasAuthourity('VIEW_UNDERWRITING_FLOWS')) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            You do not have permission to view Payment Gateway Configs.
          </Typography>
        </Box>
      );
    }

    const {
      loader, gateway_configs, get_configs_error,
      config_raw_loader, get_config_raw_error,
      publish_loader, publish_error,
      create_loader, create_error,
    } = this.props;
    const { editorOpen, isNew, selectedConfig, editorContent, hasYamlErrors, activeTab } = this.state;

    const saving = publish_loader || create_loader;
    const saveError = publish_error || create_error;

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentsOutlinedIcon sx={{ color: '#6B7280' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
              Payment Gateway Configs
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => this.openCreate()}
            sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' }, textTransform: 'none' }}
          >
            New Gateway
          </Button>
        </Box>

        {get_configs_error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load gateway configs. Please try again.
          </Alert>
        )}

        {loader ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ border: '1px solid #E5E7EB', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={TH}>ID</TableCell>
                  <TableCell sx={TH}>Partner</TableCell>
                  <TableCell sx={TH}>Channel</TableCell>
                  <TableCell sx={TH}>Type</TableCell>
                  <TableCell sx={TH}>Enabled</TableCell>
                  <TableCell sx={TH} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!gateway_configs || gateway_configs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ ...TD, textAlign: 'center', color: '#9CA3AF', py: 4 }}>
                      No gateway configs found for this partner.
                    </TableCell>
                  </TableRow>
                ) : (
                  gateway_configs.map(config => (
                    <TableRow key={config.id} hover>
                      <TableCell sx={{ ...TD, fontFamily: 'monospace', fontSize: 12, fontWeight: 500 }}>{config.id}</TableCell>
                      <TableCell sx={{ ...TD, fontFamily: 'monospace', fontSize: 12 }}>{config.partner}</TableCell>
                      <TableCell sx={TD}>{config.channel || '—'}</TableCell>
                      <TableCell sx={TD}>
                        {config.type ? <Chip label={config.type} size="small" sx={{ fontSize: 11 }} /> : '—'}
                      </TableCell>
                      <TableCell sx={TD}>
                        <Chip
                          label={config.enabled ? 'enabled' : 'disabled'}
                          size="small"
                          color={config.enabled ? 'success' : 'default'}
                          sx={{ fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell sx={TD} align="right">
                        <Tooltip title="Edit YAML">
                          <IconButton size="small" onClick={() => this.openEdit(config)} sx={{ color: '#0D47A1' }}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={editorOpen}
          onClose={() => this.closeEditor()}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { height: '85vh' } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {isNew ? 'New Gateway Config' : (selectedConfig && selectedConfig.id)}
              </Typography>
              {!isNew && selectedConfig && (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6B7280' }}>
                  {selectedConfig.partner}{selectedConfig.channel ? ` · ${selectedConfig.channel}` : ''}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => this.closeEditor()}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
            {saveError && (
              <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                {(saveError && saveError.message) || 'Failed to save gateway config. Check your YAML is valid.'}
              </Alert>
            )}
            {get_config_raw_error && (
              <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                Failed to load gateway config content.
              </Alert>
            )}

            {config_raw_loader ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => this.setState({ activeTab: v })}
                  sx={{ px: 2, borderBottom: '1px solid #E5E7EB', minHeight: 40 }}
                >
                  <Tab label="UI" sx={{ minHeight: 40, textTransform: 'none', fontSize: 13 }} />
                  <Tab label="YAML" sx={{ minHeight: 40, textTransform: 'none', fontSize: 13 }} />
                </Tabs>
                {activeTab === 0 ? (
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                    {this.renderUiTab()}
                  </Box>
                ) : (
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <AceEditor
                      mode="yaml"
                      theme="monokai"
                      value={editorContent}
                      onChange={value => this.setState({ editorContent: value })}
                      onValidate={annotations => this.setState({ hasYamlErrors: annotations.some(a => a.type === 'error') })}
                      name="gateway-config-yaml-editor"
                      width="100%"
                      height="100%"
                      fontSize={13}
                      showPrintMargin={false}
                      showGutter={true}
                      highlightActiveLine={true}
                      editorProps={{ $blockScrolling: true }}
                      setOptions={{
                        enableBasicAutocompletion: true,
                        enableLiveAutocompletion: false,
                        showLineNumbers: true,
                        tabSize: 2,
                        useWorker: true,
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #E5E7EB' }}>
            <Button onClick={() => this.closeEditor()} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlinedIcon />}
              onClick={() => this.handlePublish()}
              disabled={saving || config_raw_loader || !editorContent || hasYamlErrors}
              sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' }, textTransform: 'none' }}
            >
              {saving ? 'Publishing…' : 'Publish'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

function mapStateToProps(state) {
  return {
    loader: state.gatewayConfigs.loader,
    gateway_configs: state.gatewayConfigs.gateway_configs,
    get_configs_error: state.gatewayConfigs.get_configs_error,

    config_raw_loader: state.gatewayConfigs.config_raw_loader,
    gateway_config_raw: state.gatewayConfigs.gateway_config_raw,
    get_config_raw_error: state.gatewayConfigs.get_config_raw_error,

    publish_loader: state.gatewayConfigs.publish_loader,
    successfully_published: state.gatewayConfigs.successfully_published,
    publish_error: state.gatewayConfigs.publish_error,

    create_loader: state.gatewayConfigs.create_loader,
    successfully_created: state.gatewayConfigs.successfully_created,
    create_error: state.gatewayConfigs.create_error,
  };
}

export default connect(mapStateToProps)(GatewayConfigs);
